/**
 * CallbackReminderHost (punch-list-3 #8) — recruiter pop-up reminders for an
 * imminent booked call. Mounted ONCE in CRMApp next to <RatingPromptHost />
 * (outside <Routes>, so it survives navigation and runs on every CRM page).
 *
 * Lead-facing reminder TEXTS are off by default (see crmCadence.js kill switch);
 * recruiters instead get a heads-up here at 10 MIN and 1 MIN before the slot.
 * Each window fires a bottom-right in-app TOAST plus a native browser/OS
 * Notification (so an unfocused CRM tab still alerts). Body = first name + time
 * only — NO PHI beyond the given name (same content-free posture as
 * crmRealtime.emitContactChange).
 *
 * Polls GET /api/crm/callbacks/upcoming every 60s and RE-DERIVES the two fire
 * windows from each callback's live scheduled_at on a short (~15s) cadence —
 * never a long setTimeout (laptop sleep would drift it). Each (callback, window)
 * fires exactly once, de-duped via an in-memory Set; the set is pruned of
 * callbacks that leave the horizon so a genuine re-book can re-fire.
 *
 * z-30: deliberately BELOW the rating toast (z-40) and softphone (z-50) so an
 * active call / Answer UI always wins.
 */
import { useEffect, useRef, useState } from 'react';
import { Phone, X } from 'lucide-react';
import api from '../../services/api';
import { UpcomingCallback } from '../types';

const POLL_MS = 60_000;
const TICK_MS = 15_000;
const AUTO_DISMISS_MS = 60_000;

export type ReminderWindow = 'm10' | 'm1';

/**
 * Pure fire-window decision, extracted so it's unit-testable without React.
 * Returns the window that is CURRENTLY due (not yet past) for the given minutes
 * until the slot, or null. A booking < 10 min out never satisfies m10, so it
 * correctly gets only the m1 pop-up.
 */
export function fireWindow(minutesUntil: number): ReminderWindow | null {
  if (minutesUntil <= 1 && minutesUntil > -1) return 'm1'; // small negative grace
  if (minutesUntil <= 10 && minutesUntil > 1) return 'm10';
  return null;
}

interface ActiveToast {
  callbackId: string;
  window: ReminderWindow;
  firstName: string | null;
  scheduledAt: string;
}

function timeLabel(scheduledAt: string): string {
  return new Date(scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function CallbackReminderHost() {
  const [upcoming, setUpcoming] = useState<UpcomingCallback[]>([]);
  const [active, setActive] = useState<ActiveToast | null>(null);
  const firedKeyRef = useRef<Set<string>>(new Set());
  const askedPermissionRef = useRef(false);

  // Lazily ask for notification permission once (NOT a hard prompt on first
  // paint — only if it's still 'default'). Guarded so a remount doesn't re-ask.
  useEffect(() => {
    if (askedPermissionRef.current) return;
    askedPermissionRef.current = true;
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {
      /* unsupported — the in-app toast still fires */
    }
  }, []);

  // Poll the slim feed every 60s. A missed poll is harmless — swallow errors.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/api/crm/callbacks/upcoming');
        if (!cancelled) setUpcoming(res.data?.callbacks || []);
      } catch {
        /* a missed poll is harmless; do not surface a banner */
      }
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Prune de-dupe keys for callbacks no longer in the horizon, so a genuine
  // re-book within the window can legitimately re-fire.
  useEffect(() => {
    const live = new Set<string>();
    for (const cb of upcoming) {
      live.add(`${cb.id}:m10`);
      live.add(`${cb.id}:m1`);
    }
    for (const key of Array.from(firedKeyRef.current)) {
      if (!live.has(key)) firedKeyRef.current.delete(key);
    }
  }, [upcoming]);

  // Re-derive the fire windows from live scheduled_at on a short cadence (and
  // immediately when `upcoming` changes) so a window arriving between polls
  // still fires promptly. NEVER cache a fire time / schedule a long setTimeout.
  useEffect(() => {
    const evaluate = () => {
      for (const cb of upcoming) {
        const minutesUntil = (new Date(cb.scheduled_at).getTime() - Date.now()) / 60000;
        const win = fireWindow(minutesUntil);
        if (!win) continue;
        const key = `${cb.id}:${win}`;
        if (firedKeyRef.current.has(key)) continue;
        firedKeyRef.current.add(key);
        // Most-imminent wins — m1 supersedes a lingering m10 toast.
        setActive({ callbackId: cb.id, window: win, firstName: cb.first_name, scheduledAt: cb.scheduled_at });
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification('Upcoming call', {
              body: `${cb.first_name || 'A patient'} — ${timeLabel(cb.scheduled_at)}`,
              tag: `cb-${win}:${cb.id}`, // OS collapses dupes across tabs
            });
          }
        } catch {
          /* denied/unsupported — the in-app toast still fires */
        }
      }
    };
    evaluate();
    const id = window.setInterval(evaluate, TICK_MS);
    return () => window.clearInterval(id);
  }, [upcoming]);

  // Auto-dismiss the toast (the de-dupe set, not the toast, prevents re-fire).
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => setActive(null), AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [active]);

  if (!active) return null;

  return (
    <div className="fixed bottom-4 right-4 z-30 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Upcoming call</p>
            <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">
              {active.firstName || 'A patient'} — {timeLabel(active.scheduledAt)}
              {active.window === 'm1' ? ' (in ~1 min)' : ' (in ~10 min)'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setActive(null)}
          aria-label="Dismiss reminder"
          className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0 sm:p-0.5"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
